// components/Dashboard/FleetMap.jsx - FIXED INFOWINDOW ERROR
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
  Polygon,
  Circle,
  Polyline
} from '@react-google-maps/api';
import { 
  Car, 
  User, 
  MapPin, 
  Navigation,
  Building,
  Layers,
  Target,
  Filter
} from 'lucide-react';

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

// Default center (Lagos)
const defaultCenter = {
  lat: 6.5244,
  lng: 3.3792
};

// Sample data for clinics and geofences
const sampleClinics = [
  { id: 1, name: 'Main Hospital', coordinates: { lat: 6.5244, lng: 3.3792 }, color: '#EF4444' },
  { id: 2, name: 'North Clinic', coordinates: { lat: 6.5344, lng: 3.3892 }, color: '#DC2626' },
  { id: 3, name: 'West Medical Center', coordinates: { lat: 6.5144, lng: 3.3592 }, color: '#EF4444' },
  { id: 4, name: 'East Health Center', coordinates: { lat: 6.5244, lng: 3.3992 }, color: '#DC2626' },
  { id: 5, name: 'South Clinic', coordinates: { lat: 6.5044, lng: 3.3692 }, color: '#EF4444' },
  { id: 6, name: 'Central Dispensary', coordinates: { lat: 6.5444, lng: 3.3692 }, color: '#DC2626' },
];

const sampleGeofences = [
  { 
    id: 1, 
    name: 'High Priority Zone', 
    coordinates: [{lat: 6.5144, lng: 3.3692}, {lat: 6.5144, lng: 3.3892}, {lat: 6.5344, lng: 3.3892}, {lat: 6.5344, lng: 3.3692}], 
    color: '#F59E0B' 
  },
  { 
    id: 2, 
    name: 'Downtown Area', 
    center: { lat: 6.5244, lng: 3.3792 }, 
    radius: 500,
    color: '#3B82F6' 
  },
  { 
    id: 3, 
    name: 'Industrial Zone', 
    coordinates: [{lat: 6.5044, lng: 3.3992}, {lat: 6.5044, lng: 3.4192}, {lat: 6.5244, lng: 3.4192}, {lat: 6.5244, lng: 3.3992}], 
    color: '#10B981' 
  },
  { 
    id: 4, 
    name: 'Residential Area', 
    center: { lat: 6.5144, lng: 3.3492 }, 
    radius: 400,
    color: '#8B5CF6' 
  },
  { 
    id: 5, 
    name: 'Commercial District', 
    coordinates: [{lat: 6.5444, lng: 3.3592}, {lat: 6.5444, lng: 3.3792}, {lat: 6.5644, lng: 3.3792}, {lat: 6.5644, lng: 3.3592}], 
    color: '#EC4899' 
  },
  { 
    id: 6, 
    name: 'University Zone', 
    center: { lat: 6.5344, lng: 3.3592 }, 
    radius: 600,
    color: '#6366F1' 
  },
];

const sampleRoutes = [
  {
    id: 1,
    name: 'Morning Delivery Route',
    coordinates: [{lat: 6.5144, lng: 3.3692}, {lat: 6.5244, lng: 3.3792}, {lat: 6.5344, lng: 3.3892}, {lat: 6.5444, lng: 3.3992}],
    color: '#3B82F6'
  },
  {
    id: 2,
    name: 'Evening Pickup Route',
    coordinates: [{lat: 6.5044, lng: 3.3692}, {lat: 6.5244, lng: 3.3792}, {lat: 6.5244, lng: 3.3992}, {lat: 6.5444, lng: 3.3692}],
    color: '#10B981'
  }
];

const FleetMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLayers, setActiveLayers] = useState({
    vehicles: true,
    drivers: true,
    clinics: true,
    geofences: true,
    routes: true
  });
  const [mapStats, setMapStats] = useState({
    vehicles: 0,
    drivers: 0,
    clinics: 6,
    geofences: 6,
    routes: 2
  });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  
  // Use refs for map state to avoid re-renders
  const mapRef = useRef(null);
  const centerRef = useRef(defaultCenter);
  const zoomRef = useRef(14);
  const isProgrammaticChange = useRef(false);

  // Color constants
  const PRIMARY_COLOR = '#e28743';
  const SECONDARY_COLOR = '#304a3f';

  // Driver status colors
  const statusColors = {
    on_duty: PRIMARY_COLOR,
    active: SECONDARY_COLOR,
    on_trip: '#8B5CF6',
    on_break: '#F59E0B',
    off_duty: '#6B7280',
    available: SECONDARY_COLOR,
    inactive: '#9CA3AF'
  };

  const vehicleStatusColors = {
    active: SECONDARY_COLOR,
    in_use: PRIMARY_COLOR,
    available: '#10B981',
    maintenance: '#F59E0B',
    inactive: '#6B7280'
  };

  // Get API base URL from .env
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api';
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Memoize map options to prevent re-renders
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: true,
    fullscreenControl: true,
    mapTypeId: mapTypeId,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), [mapTypeId]);

  // ===== FETCH DATA =====
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }

      // 1. Get drivers
      try {
        const driversResponse = await fetch(`${API_BASE_URL}/drivers/active`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (driversResponse.ok) {
          const driversData = await driversResponse.json();
          if (driversData.success) {
            setDrivers(driversData.data);
            setMapStats(prev => ({ ...prev, drivers: driversData.data.length }));
          }
        }
      } catch (driversError) {
        console.error('Error fetching drivers:', driversError);
      }
      
      // 2. Get vehicles
      try {
        const vehiclesResponse = await fetch(`${API_BASE_URL}/vehicles`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (vehiclesResponse.ok) {
          const vehiclesData = await vehiclesResponse.json();
          if (vehiclesData.success) {
            setVehicles(vehiclesData.data);
            setMapStats(prev => ({ ...prev, vehicles: vehiclesData.data.length }));
          }
        }
      } catch (vehicleError) {
        console.log('Vehicles endpoint optional');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // ===== MAP LOAD CALLBACK =====
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // ===== CREATE ICONS =====
  const createIcon = useCallback((color, type = 'driver', isSelected = false) => {
    let path;
    let scale = 1;
    
    switch(type) {
      case 'vehicle':
        path = 'M18.92,6.01C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.29,5.42 5.08,6.01L3,12V20C3,20.55 3.45,21 4,21H5C5.55,21 6,20.55 6,20V19H18V20C18,20.55 18.45,21 19,21H20C20.55,21 21,20.55 21,20V12L18.92,6.01ZM6.5,16C5.67,16 5,15.33 5,14.5C5,13.67 5.67,13 6.5,13C7.33,13 8,13.67 8,14.5C8,15.33 7.33,16 6.5,16ZM17.5,16C16.67,16 16,15.33 16,14.5C16,13.67 16.67,13 17.5,13C18.33,13 19,13.67 19,14.5C19,15.33 18.33,16 17.5,16ZM5,11L6.5,6.5H17.5L19,11H5Z';
        scale = 1.2;
        break;
      case 'clinic':
        path = 'M18,14H14V18H10V14H6V10H10V6H14V10H18M19,3H5C3.89,3 3,3.89 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.89 20.1,3 19,3Z';
        scale = 1;
        break;
      default: // driver
        path = window.google?.maps.SymbolPath.CIRCLE;
        scale = isSelected ? 1.2 : 1;
        break;
    }
    
    return {
      path,
      fillColor: color,
      fillOpacity: type === 'driver' ? 0.8 : 0.9,
      strokeColor: '#FFFFFF',
      strokeWeight: type === 'driver' ? 2 : 1,
      scale
    };
  }, []);

  // ===== GET POSITION FOR SELECTED MARKER =====
  const getSelectedMarkerPosition = useCallback(() => {
    if (!selectedMarker) return null;
    
    switch(selectedMarker.type) {
      case 'geofence':
        return selectedMarker.data.center || 
               (selectedMarker.data.coordinates && selectedMarker.data.coordinates[0]) || 
               defaultCenter;
      case 'route':
        return selectedMarker.data.coordinates && selectedMarker.data.coordinates[0] || defaultCenter;
      case 'clinic':
        return selectedMarker.data.coordinates || defaultCenter;
      case 'vehicle':
      case 'driver':
        return selectedMarker.data.position || defaultCenter;
      default:
        return defaultCenter;
    }
  }, [selectedMarker]);

  // ===== FILTER DATA WITH LOCATIONS =====
  const driversWithLocations = useMemo(() => drivers.filter(driver => 
    driver.currentLocation && 
    driver.currentLocation.coordinates && 
    driver.currentLocation.coordinates[0] !== 0 && 
    driver.currentLocation.coordinates[1] !== 0
  ), [drivers]);

  const vehiclesWithLocations = useMemo(() => vehicles.filter(vehicle => 
    vehicle.currentLocation && 
    vehicle.currentLocation.coordinates && 
    vehicle.currentLocation.coordinates[0] !== 0 && 
    vehicle.currentLocation.coordinates[1] !== 0
  ), [vehicles]);

  // ===== FIT TO BOUNDS FUNCTION =====
  const fitToBounds = useCallback(() => {
    if (!mapRef.current) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    let hasBounds = false;

    // Add drivers to bounds
    if (activeLayers.drivers && driversWithLocations.length > 0) {
      driversWithLocations.forEach(driver => {
        const [lng, lat] = driver.currentLocation.coordinates;
        bounds.extend({ lat, lng });
        hasBounds = true;
      });
    }

    // Add vehicles to bounds
    if (activeLayers.vehicles && vehiclesWithLocations.length > 0) {
      vehiclesWithLocations.forEach(vehicle => {
        const [lng, lat] = vehicle.currentLocation.coordinates;
        bounds.extend({ lat, lng });
        hasBounds = true;
      });
    }

    // Add clinics to bounds
    if (activeLayers.clinics && sampleClinics.length > 0) {
      sampleClinics.forEach(clinic => {
        bounds.extend(clinic.coordinates);
        hasBounds = true;
      });
    }

    if (hasBounds) {
      isProgrammaticChange.current = true;
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 }
      });
      
      setTimeout(() => {
        isProgrammaticChange.current = false;
      }, 300);
    }
  }, [activeLayers, driversWithLocations, vehiclesWithLocations]);

  // ===== RESET VIEW =====
  const resetView = useCallback(() => {
    if (mapRef.current) {
      isProgrammaticChange.current = true;
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(14);
      centerRef.current = defaultCenter;
      zoomRef.current = 14;
      
      setTimeout(() => {
        isProgrammaticChange.current = false;
      }, 100);
    }
  }, []);

  // ===== RENDER =====
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex items-center justify-center h-64 sm:h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 sm:h-12 w-10 sm:w-12 border-b-2 mx-auto mb-3 sm:mb-4" style={{ borderColor: PRIMARY_COLOR }}></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading fleet data...</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedMarkerPosition = getSelectedMarkerPosition();

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Fleet Operations Map</h2>
          <p className="text-gray-600 text-sm sm:text-base">Track all vehicles, drivers, clinics, and geofences in real-time</p>
        </div>
        
        {/* Map Type Controls */}
        <div className="flex flex-wrap gap-2">
          <div className="flex space-x-1">
            <button 
              onClick={() => setMapTypeId('roadmap')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'roadmap' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'roadmap' ? PRIMARY_COLOR : undefined }}
            >
              Road
            </button>
            <button 
              onClick={() => setMapTypeId('satellite')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'satellite' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'satellite' ? PRIMARY_COLOR : undefined }}
            >
              Satellite
            </button>
            <button 
              onClick={() => setMapTypeId('hybrid')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'hybrid' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'hybrid' ? PRIMARY_COLOR : undefined }}
            >
              Hybrid
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Left Sidebar - Map Layers */}
        <div className="lg:col-span-1 space-y-4 sm:space-y-6">
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <Layers className="w-4 h-4" style={{ color: PRIMARY_COLOR }} />
              <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Map Layers</h3>
            </div>
            
            <div className="space-y-2 sm:space-y-3">
              {/* Vehicles Layer */}
              <div className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3" style={{ 
                    backgroundColor: `${SECONDARY_COLOR}15`
                  }}>
                    <Car className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: SECONDARY_COLOR }} />
                  </div>
                  <div>
                    <p className="font-medium text-xs sm:text-sm">Vehicles</p>
                    <p className="text-xs text-gray-500">{mapStats.vehicles} active</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={activeLayers.vehicles}
                    onChange={() => setActiveLayers(prev => ({ ...prev, vehicles: !prev.vehicles }))}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] sm:after:top-[2px] after:left-[1px] sm:after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Drivers Layer */}
              <div className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3" style={{ 
                    backgroundColor: `${PRIMARY_COLOR}15`
                  }}>
                    <User className="w-3 sm:w-4 h-3 sm:h-4" style={{ color: PRIMARY_COLOR }} />
                  </div>
                  <div>
                    <p className="font-medium text-xs sm:text-sm">Drivers</p>
                    <p className="text-xs text-gray-500">{mapStats.drivers} on duty</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={activeLayers.drivers}
                    onChange={() => setActiveLayers(prev => ({ ...prev, drivers: !prev.drivers }))}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] sm:after:top-[2px] after:left-[1px] sm:after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all" style={{ backgroundColor: activeLayers.drivers ? PRIMARY_COLOR : undefined }}></div>
                </label>
              </div>

              {/* Routes Layer */}
              <div className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 bg-indigo-100">
                    <Navigation className="w-3 sm:w-4 h-3 sm:h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-xs sm:text-sm">Routes</p>
                    <p className="text-xs text-gray-500">{mapStats.routes} active</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={activeLayers.routes}
                    onChange={() => setActiveLayers(prev => ({ ...prev, routes: !prev.routes }))}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] sm:after:top-[2px] after:left-[1px] sm:after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              {/* Clinics Layer */}
              <div className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 bg-red-100">
                    <Building className="w-3 sm:w-4 h-3 sm:h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-xs sm:text-sm">Clinics</p>
                    <p className="text-xs text-gray-500">{mapStats.clinics} locations</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={activeLayers.clinics}
                    onChange={() => setActiveLayers(prev => ({ ...prev, clinics: !prev.clinics }))}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] sm:after:top-[2px] after:left-[1px] sm:after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>

              {/* Geofences Layer */}
              <div className="flex items-center justify-between p-2 bg-white rounded border">
                <div className="flex items-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 bg-purple-100">
                    <MapPin className="w-3 sm:w-4 h-3 sm:h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-xs sm:text-sm">Geofences</p>
                    <p className="text-xs text-gray-500">{mapStats.geofences} zones</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={activeLayers.geofences}
                    onChange={() => setActiveLayers(prev => ({ ...prev, geofences: !prev.geofences }))}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] sm:after:top-[2px] after:left-[1px] sm:after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Target className="w-4 h-4" style={{ color: PRIMARY_COLOR }} />
                <h4 className="font-medium text-gray-700 text-sm sm:text-base">Legend</h4>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div className="flex items-center">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-1 sm:mr-2" style={{ backgroundColor: SECONDARY_COLOR }}></div>
                  <span className="text-xs text-gray-600">Vehicles</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-1 sm:mr-2" style={{ backgroundColor: PRIMARY_COLOR }}></div>
                  <span className="text-xs text-gray-600">Drivers</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-1 sm:mr-2 bg-red-500"></div>
                  <span className="text-xs text-gray-600">Clinics</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-1 sm:w-4 sm:h-1 bg-indigo-500 mr-1 sm:mr-2"></div>
                  <span className="text-xs text-gray-600">Routes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Filter className="w-4 h-4" style={{ color: SECONDARY_COLOR }} />
              <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Summary</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="bg-white p-2 sm:p-3 rounded border text-center">
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{mapStats.vehicles}</p>
                <p className="text-xs text-gray-600">Vehicles</p>
              </div>
              <div className="bg-white p-2 sm:p-3 rounded border text-center">
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{mapStats.drivers}</p>
                <p className="text-xs text-gray-600">Drivers</p>
              </div>
              <div className="bg-white p-2 sm:p-3 rounded border text-center">
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{mapStats.routes}</p>
                <p className="text-xs text-gray-600">Routes</p>
              </div>
              <div className="bg-white p-2 sm:p-3 rounded border text-center">
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{mapStats.geofences}</p>
                <p className="text-xs text-gray-600">Zones</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Map */}
        <div className="lg:col-span-3">
          <div className="relative rounded-lg overflow-hidden border border-gray-200 h-[350px] sm:h-[400px]">
            <LoadScript
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
              loadingElement={<div className="h-full w-full bg-gray-100 animate-pulse" />}
            >
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={centerRef.current}
                zoom={zoomRef.current}
                options={mapOptions}
                onLoad={onLoad}
              >
                {/* Geofences (if layer is active) */}
                {activeLayers.geofences && sampleGeofences.map((geofence) => {
                  if (geofence.coordinates) {
                    // Polygon geofence
                    return (
                      <Polygon
                        key={`geofence-${geofence.id}`}
                        paths={geofence.coordinates}
                        options={{
                          fillColor: geofence.color,
                          fillOpacity: 0.1,
                          strokeColor: geofence.color,
                          strokeOpacity: 0.8,
                          strokeWeight: 2
                        }}
                        onClick={() => setSelectedMarker({ type: 'geofence', data: geofence })}
                      />
                    );
                  } else if (geofence.center && geofence.radius) {
                    // Circle geofence
                    return (
                      <Circle
                        key={`geofence-${geofence.id}`}
                        center={geofence.center}
                        radius={geofence.radius}
                        options={{
                          fillColor: geofence.color,
                          fillOpacity: 0.1,
                          strokeColor: geofence.color,
                          strokeOpacity: 0.8,
                          strokeWeight: 2
                        }}
                        onClick={() => setSelectedMarker({ type: 'geofence', data: geofence })}
                      />
                    );
                  }
                  return null;
                })}

                {/* Routes (if layer is active) */}
                {activeLayers.routes && sampleRoutes.map((route) => (
                  <Polyline
                    key={`route-${route.id}`}
                    path={route.coordinates}
                    options={{
                      strokeColor: route.color,
                      strokeOpacity: 0.7,
                      strokeWeight: 3
                    }}
                    onClick={() => setSelectedMarker({ type: 'route', data: route })}
                  />
                ))}

                {/* Clinics (if layer is active) */}
                {activeLayers.clinics && sampleClinics.map((clinic) => (
                  <Marker
                    key={`clinic-${clinic.id}`}
                    position={clinic.coordinates}
                    icon={createIcon(clinic.color, 'clinic')}
                    onClick={() => setSelectedMarker({ type: 'clinic', data: clinic })}
                  />
                ))}

                {/* Vehicles (if layer is active) */}
                {activeLayers.vehicles && vehiclesWithLocations.map((vehicle) => {
                  const [lng, lat] = vehicle.currentLocation.coordinates;
                  const position = { lat, lng };
                  const vehicleName = vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : vehicle.vehicleNumber || 'Vehicle';
                  const color = vehicleStatusColors[vehicle.status] || '#6B7280';
                  
                  return (
                    <Marker
                      key={`vehicle-${vehicle.id}`}
                      position={position}
                      icon={createIcon(color, 'vehicle')}
                      onClick={() => setSelectedMarker({ 
                        type: 'vehicle', 
                        data: { ...vehicle, position, vehicleName, color } 
                      })}
                    />
                  );
                })}

                {/* Drivers (if layer is active) */}
                {activeLayers.drivers && driversWithLocations.map((driver) => {
                  const [lng, lat] = driver.currentLocation.coordinates;
                  const position = { lat, lng };
                  const driverName = driver.name || driver.user?.profile?.name || driver.user?.name || 'Driver';
                  const color = statusColors[driver.status] || '#6B7280';
                  
                  return (
                    <Marker
                      key={`driver-${driver.id}`}
                      position={position}
                      icon={createIcon(color, 'driver')}
                      onClick={() => setSelectedMarker({ 
                        type: 'driver', 
                        data: { ...driver, position, driverName, color } 
                      })}
                    />
                  );
                })}

                {/* InfoWindow for selected marker */}
                {selectedMarker && selectedMarkerPosition && (
                  <InfoWindow
                    position={selectedMarkerPosition}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div className="p-2 min-w-[180px] sm:min-w-[220px]">
                      {selectedMarker.type === 'geofence' && (
                        <>
                          <h3 className="font-bold text-sm mb-1">{selectedMarker.data.name}</h3>
                          <p className="text-xs text-gray-600">Geofence Zone</p>
                          {selectedMarker.data.radius && (
                            <p className="text-xs text-gray-600">Radius: {selectedMarker.data.radius}m</p>
                          )}
                        </>
                      )}
                      
                      {selectedMarker.type === 'route' && (
                        <>
                          <h3 className="font-bold text-sm mb-1">{selectedMarker.data.name}</h3>
                          <p className="text-xs text-gray-600">Route with {selectedMarker.data.coordinates.length} waypoints</p>
                        </>
                      )}
                      
                      {selectedMarker.type === 'clinic' && (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-sm">{selectedMarker.data.name}</h3>
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                              CLINIC
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            <MapPin className="inline w-3 h-3 mr-1" />
                            {selectedMarker.data.coordinates.lat.toFixed(4)}, {selectedMarker.data.coordinates.lng.toFixed(4)}
                          </p>
                        </>
                      )}
                      
                      {selectedMarker.type === 'vehicle' && (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-sm">{selectedMarker.data.vehicleName}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              selectedMarker.data.status === 'active' ? 'text-white' :
                              selectedMarker.data.status === 'in_use' ? 'text-white' :
                              'bg-gray-100 text-gray-800'
                            }`} style={{ 
                              backgroundColor: selectedMarker.data.status === 'in_use' ? PRIMARY_COLOR : 
                                           selectedMarker.data.status === 'active' ? SECONDARY_COLOR : undefined 
                            }}>
                              {selectedMarker.data.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            <Car className="inline w-3 h-3 mr-1" />
                            Plate: {selectedMarker.data.licensePlate || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-600">Type: {selectedMarker.data.vehicleType || 'N/A'}</p>
                          {selectedMarker.data.currentLocation?.address && (
                            <p className="text-xs text-gray-600">
                              <MapPin className="inline w-3 h-3 mr-1" />
                              {selectedMarker.data.currentLocation.address}
                            </p>
                          )}
                        </>
                      )}
                      
                      {selectedMarker.type === 'driver' && (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-sm">{selectedMarker.data.driverName}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              selectedMarker.data.status === 'active' ? 'text-white' :
                              selectedMarker.data.status === 'on_duty' ? 'text-white' :
                              'bg-gray-100 text-gray-800'
                            }`} style={{ 
                              backgroundColor: selectedMarker.data.status === 'on_duty' ? PRIMARY_COLOR : 
                                           selectedMarker.data.status === 'active' ? SECONDARY_COLOR : undefined 
                            }}>
                              {selectedMarker.data.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            <User className="inline w-3 h-3 mr-1" />
                            License: {selectedMarker.data.licenseNumber || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-600">Type: {selectedMarker.data.driverType || 'N/A'}</p>
                          {selectedMarker.data.currentLocation?.address && (
                            <p className="text-xs text-gray-600">
                              <MapPin className="inline w-3 h-3 mr-1" />
                              {selectedMarker.data.currentLocation.address}
                            </p>
                          )}
                        </>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-2">Click outside to close</p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </LoadScript>

            {/* Map Controls */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-white rounded-lg shadow-sm p-1.5 sm:p-2 flex space-x-1.5 sm:space-x-2">
              <button 
                onClick={() => {
                  if (mapRef.current) {
                    isProgrammaticChange.current = true;
                    mapRef.current.setZoom(mapRef.current.getZoom() + 1);
                    setTimeout(() => {
                      isProgrammaticChange.current = false;
                    }, 100);
                  }
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 rounded text-sm sm:text-base"
              >
                +
              </button>
              <button 
                onClick={() => {
                  if (mapRef.current) {
                    isProgrammaticChange.current = true;
                    mapRef.current.setZoom(mapRef.current.getZoom() - 1);
                    setTimeout(() => {
                      isProgrammaticChange.current = false;
                    }, 100);
                  }
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 rounded text-sm sm:text-base"
              >
                -
              </button>
            </div>

            <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 bg-white rounded-lg shadow-sm px-2.5 sm:px-3 py-1.5 sm:py-2">
              <p className="text-xs sm:text-sm font-medium text-gray-700">
                Showing: 
                <span className="ml-2">
                  {activeLayers.vehicles && <Car className="inline w-3 h-3 mr-1" />}
                  {activeLayers.drivers && <User className="inline w-3 h-3 mr-1" />}
                  {activeLayers.clinics && <Building className="inline w-3 h-3 mr-1" />}
                  {activeLayers.geofences && <MapPin className="inline w-3 h-3 mr-1" />}
                  {activeLayers.routes && <Navigation className="inline w-3 h-3 mr-1" />}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetMap;